// FREE-tier upgrade tease. Shown in lieu of the per-row members list when
// the API responds with `upgradeRequired=true`. Renders the per-stage
// counts that the API DOES return for FREE plan users so the user gets a
// hint of what they'd see post-upgrade, then a single CTA.
//
// The CTA is a no-op for now — TASK-036 (Stars invoice) wires up the
// onUpgrade callback. The feature owns the visual; the page picks the
// behavior.

import {
  MEMBER_LIFECYCLE_STAGES,
  type MemberLifecycleStage,
  type MemberStageCounts,
} from "@community-analytics/shared";

import { stageStyle } from "@entities/member";
import { cn } from "@shared/lib";
import { Button } from "@shared/ui";

export interface UpgradeTeaseProps {
  /** Per-stage counts (always returned by the API, FREE included). */
  counts: MemberStageCounts;
  /** Externalized stage labels. */
  labelsByStage: Readonly<Record<MemberLifecycleStage, string>>;
  /** Title text — "Upgrade to Pro to see individual members". */
  title: string;
  body: string;
  ctaLabel: string;
  /** Optional click handler — wires to TASK-036 Stars invoice in pages. */
  onUpgrade?: () => void;
  className?: string;
}

export function UpgradeTease({
  counts,
  labelsByStage,
  title,
  body,
  ctaLabel,
  onUpgrade,
  className,
}: UpgradeTeaseProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-dashed bg-background p-6",
        className,
      )}
      role="region"
      aria-label={title}
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
      <ul className="grid grid-cols-2 gap-2">
        {MEMBER_LIFECYCLE_STAGES.map((stage) => {
          const style = stageStyle(stage);
          return (
            <li
              key={stage}
              className={cn(
                "flex items-center justify-between rounded-md border px-3 py-2 text-xs",
                style.backgroundClass,
                style.borderClass,
              )}
            >
              <span className={cn("font-medium uppercase", style.textClass)}>
                {labelsByStage[stage]}
              </span>
              <span className={cn("tabular-nums", style.textClass)}>
                {counts[stage]}
              </span>
            </li>
          );
        })}
      </ul>
      <Button
        type="button"
        onClick={onUpgrade}
        disabled={onUpgrade === undefined}
        className="self-start"
      >
        {ctaLabel}
      </Button>
    </div>
  );
}
